<template>
    <div class="p-3 w-100">
        <div class="search-title">
            <h1 class="mt-0">Search Results for "{{ searchStr }}"</h1>
        </div>
        <div v-if="isLoading" class="py-4">
            <h5>Loading results...</h5>
        </div>
        <div class="search-results" v-else-if="results.length != 0">
            <div v-for="result in getSearchResultsPage(results, page)" :key="result.name" class="card bg-light my-3">
                <div class="card-body">
                    <a class="card-title" :href="result.item.path">
                        <h5 :href="result.item.path">{{ result.item.name }}</h5>
                    </a>
                    <p class="card-text" v-html="getContentBlurb(result.item.content)" />
                </div>
            </div>
            <nav aria-label="Page navigation example">
                <ul class="pagination justify-content-end">
                    <li class="page-item" :class="{ 'disabled': page <= 1 }">
                        <a class="page-link" :href="'#' + (page - 1)">Previous</a>
                    </li>
                    <li class="page-item" 
                        :class="{ 'active': page === (i + 1), 'text-light': page === (i + 1) }"
                        v-for="i in countResultPages(results)" 
                        :key="i">
                        <a class="page-link" :href="'#' + (i + 1)">{{ i + 1 }}</a>
                    </li>
                    <li class="page-item" :class="{ 'disabled': page >= pageCount }">
                        <a class="page-link" :href="'#' + (page + 1)">Next</a>
                    </li>
                </ul>
            </nav>
        </div>
        <div v-else>
            <h5 class="py-4">No results found.</h5>
        </div>
    </div>
</template>

<script>
import Fuse from 'fuse.js'
import PageList from '@/PageList.js'

// Get HTML Content
async function loadPageContent(htmlFile) {
    if (!htmlFile) return "<!-- No HTML File for this page -->";
    return (await import(`@/pages/${htmlFile}`)).default;
}

async function getHTMLContent() {
    // Get the HTML content for all pages
    const contentPromises = PageList.map(async (page) => {
        if (page.htmlContent !== undefined) {
            const content = await loadPageContent(page.htmlContent);
            return { ...page, content };
        }
        return page;
    });
    
    return Promise.all(contentPromises);
}

export default {
    data() {
        return {
            searchStr: '',
            results: [],
            pageCount: 0,
            pages: [],
            isLoading: true,
            page: 1
        }
    },
    async created() {
        // Load content when component is created
        try {
            this.pages = await getHTMLContent();
            this.searchStr = this.$route.query.q;
            this.performSearch();
        } catch (error) {
            console.error('Error loading page content:', error);
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        performSearch() {
            if (!this.searchStr || !this.pages.length) {
                this.results = [];
                return;
            }

            const options = {
                isCaseSensitive: false,
                includeScore: true,
                shouldSort: true,
                threshold: 0.2,
                ignoreLocation: true,
                distance: 100,
                maxPatternLength: 32,
                minMatchCharLength: 3,
                keys: [
                    { name: 'name', weight: 1 },
                    { name: 'keywords', weight: 1 },
                    { name: 'content', weight: 0.7 },
                    { name: 'keyPoints.text', weight: 0.4 },
                    { name: 'scenarios.description', weight: 0.2 }
                ]
            };

            const fuse = new Fuse(this.pages, options);
            this.results = fuse.search(this.searchStr);
            this.results.sort((a, b) => a.score - b.score);
        },
        countResultPages(results) {
            let count = results.length / 10
            let pageCount = Math.ceil(count)

            // Capping the number of pages, helps with the mobile page nav UI
            // I don't think anyone is going to need more than 50 results..
            if (pageCount > 5) {
                pageCount = 5
            }
            // returns an array of page numbers
            return Array.from(Array(pageCount).keys())
        },
        getSearchResultsPage(results, page) {
            // Return a subset of results based on page number
            // Page number is 1-indexed
            let start = (page - 1) * 10
            let end = start + 10
            return results.slice(start, end)
        },
        getContentBlurb(content) {
            if (typeof content !== 'string') {
                console.error('getContentBlurb called with non-string argument:', content);
                return '';
            }
            content = content.replace(/<[^>]*>?/gm, "")
            return content.substring(0, 500) + '...';
        },
    },
    watch: {
        '$route.hash': function (newVal, oldVal) {
            let hash = newVal
            let page = hash.replace('#', '')

            if (isNaN(parseInt(page))) {
                this.page = 1
            } else {
                this.page = parseInt(page)
            }
        },
        '$route.query.q': {
            handler(newVal) {
                this.searchStr = newVal;
                this.performSearch();
            },
            immediate: true
        }
    },
}
</script>

<style>
.search-title {
    width: 98%;
    border: 0;
    border-bottom: 1px solid rgba(0, 0, 0, .25);
}

.search-results {
    max-width: 80rem;
}
</style>